import { useNavigate, useParams } from 'react-router-dom'
import DtcDependencyForm from './form'
import { useContext, useEffect, useState } from 'react'
import Button from '../../../common/Button'
import {
  deleteRequest,
  getRequest,
  postRequest,
} from '../../../../services/base'
import { NavContext } from '../../../../context/global_context'
import { notifyError } from '../../../../utils/notifications'
import DtcDependencyStore, {
  DtcDependencyContext,
  SHOW_DEPENDENCY_MODAL,
} from '../DtcEdit/context/context'
import { Card, Flex, Form, Modal, Row, Space, Table, Typography } from 'antd'
import DtcsHeader from '../DtcsHeader'
import { ExclamationCircleOutlined } from '@ant-design/icons'
import { useIntl } from 'react-intl'

const DtcAddDependencies = (props) => {
  let { dtc_id, vehicle } = useParams()
  const intl = useIntl()
  const [state, dispatch] = useContext(DtcDependencyContext)
  const [navState, navDispatch] = useContext(NavContext)
  const [dependentDtcs, setDependentDtcs] = useState([])
  const [modal, contextHolder] = Modal.useModal()
  const navigate = useNavigate()

  useEffect(() => {
    getDtcsList()
    getDependentDtc()
  }, [])

  const getDtcsList = async () => {
    navDispatch({
      type: 'loader',
      data: {
        caption: intl.formatMessage({ id: 'dtcs.clone.fetchingData' }),
        showLoader: true,
      },
      notification: {},
    })

    let dtcs_res = await getRequest(`/api/dtcs/${dtc_id}/?vehicle=${vehicle}`)

    navDispatch({
      type: 'header',
      data: {
        header: intl.formatMessage({ id: 'layout.adminTools' }),
        sub_header: `Vehicle List / ${vehicle} / DTCs / Tracer Authoring Mappings / Add Dependency`,
        id: '',
        show_back_btn: true,
        dtc_details: [dtcs_res],
        vehicle: vehicle,
        back_btn_url: `/vehicles/${vehicle}/dtcs/listing`,
      },
    })
  }

  const getDependentDtc = async () => {
    let res = await getRequest(
      `/api/dtcs/dependent_dtcs/${dtc_id}/?vehicle=${vehicle}`
    )
    // base.js returns {status: false} instead of rejecting. Falling through put
    // `undefined` in state, which crashed on the next render at [...dependentDtcs].
    if (res?.status === false) {
      notifyError(navDispatch, intl.formatMessage({ id: 'dtcs.dependencies.loadError' }))
      return
    }
    setDependentDtcs(res.data || [])
  }

  const submitDependentDtc = async (values) => {
    const data = { ...values }
    const formData = new FormData()

    Object.entries(data).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach((item) => {
          formData.append(key, item)
        })
      } else if (value !== undefined && value !== null) {
        formData.append(key, value)
      }
    })
    formData.append('vehicle', vehicle)

    let res = await postRequest(
      `/api/dtcs/save_dependent_dtcs/${dtc_id}/`,
      formData
    )
    dispatch({ type: SHOW_DEPENDENCY_MODAL, data: false })

    navDispatch({
      type: 'notification',
      data: {
        notification_type: 'success',
        msg: intl.formatMessage({ id: 'dtcs.dependencies.addSuccess' }),
      },
    })
    getDependentDtc()
  }

  const confirmDelete = (id, name) => {
    modal.confirm({
      title: intl.formatMessage({ id: 'dtcs.dependencies.confirmTitle' }),
      icon: <ExclamationCircleOutlined />,
      content: intl.formatMessage({ id: 'dtcs.dependencies.confirmDelete' }, { name }),
      onOk: () => {
        onDelete(id)
      },
      centered: true,
      maskClosable: true,
    })
  }

  const onDelete = async (id) => {
    let res = await deleteRequest(`/api/dtcs/delete_dependent_dtcs/${id}/`)
    if (res.status) {
      let tempDtcs = [...dependentDtcs]
      const newDtc = tempDtcs.filter((d) => d.id !== id)
      setDependentDtcs(newDtc)
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'success',
          msg: intl.formatMessage({ id: 'dtcs.dependencies.deleteSuccess' }),
        },
      })
    } else {
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'error',
          msg: intl.formatMessage({ id: 'dtcs.dependencies.deleteError' }),
        },
      })
    }
  }

  const dataSource = dependentDtcs.map((dtc, index) => {
    return {
      id: dtc.id,
      dtcName: dtc.name,
      description: dtc.description,
      ecu: dtc.ecu,

      dependant_id: dtc.dependant_id,
    }
  })

  const columns = [
    {
      title: intl.formatMessage({ id: 'dtcs.dependencies.dtcName' }),
      dataIndex: 'dtcName',
      align: 'left',
      sorter: (a, b) => a.dtcName.localeCompare(b.dtcName),
    },
    {
      title: intl.formatMessage({ id: 'dtcs.dependencies.description' }),
      dataIndex: 'description',
      align: 'left',
      width: 'auto',
    },
    {
      title: intl.formatMessage({ id: 'dtcs.dependencies.ecu' }),
      dataIndex: 'ecu',
      align: 'left',
    },
    {
      title: '',
      width: '2%',
      align: 'right',
      render: (_, record) => {
        return (
          <Button
            type="link"
            className="link-button danger"
            onClick={() => {
              confirmDelete(record.id, record.dtcName)
            }}
            testId="dtc-dependency-delete"
          >
            {intl.formatMessage({ id: 'dtcs.dependencies.delete' })}
          </Button>
        )
      },
    },
  ]

  return (
    <div className="page dtc-add-dependency">
      <Row className="title">
        <Flex vertical gap={16} style={{ width: '100%' }}>
          {navState?.dtc_details?.length > 0 && (
            <DtcsHeader
              dtcs={navState?.dtc_details}
              vehicleSid={navState?.vehicle}
            />
          )}

          <Flex align="baseline" justify="space-between">
            <Typography.Title level={5}>
              {navState?.dtc_details &&
                navState?.dtc_details.length > 0 &&
                `${navState?.dtc_details[0]?.ecu} - ${navState?.dtc_details[0]?.name} Dependencies`}
            </Typography.Title>

            <Space gap={16}>
              <Button
                htmlType="submit"
                type="primary"
                onClick={(e) => {
                  dispatch({ type: SHOW_DEPENDENCY_MODAL, data: true })
                }}
                testId="dtc-dependency-add"
              >
                {intl.formatMessage({ id: 'dtcs.dependencies.addNewDependency' })}
              </Button>

              <Button
                className="cancel-button"
                onClick={(e) => navigate(`/vehicles/${vehicle}/dtcs/listing`)}
              >
                {intl.formatMessage({ id: 'forms.cancel' })}
              </Button>
            </Space>
          </Flex>
        </Flex>
      </Row>
      <Row className="body">
        <Card title="" variant="outlined">
          <Flex gap={16} vertical style={{ padding: '16px 0px 16px 0px' }}>
            <DtcDependencyForm
              dependentDtcs={dependentDtcs}
              submitDependentDtc={submitDependentDtc}
            />
            <Table
              rowKey="id"
              columns={columns}
              dataSource={dataSource}
              showSorterTooltip={false}
              pagination={
                dependentDtcs.length > 10
                  ? {
                      total: dependentDtcs.length,
                      pageSize: 10,
                      showSizeChanger: false,
                    }
                  : false
              }
            />
          </Flex>
        </Card>
      </Row>

      {contextHolder}
    </div>
  )
}

export default DtcAddDependencies
